import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabaseClient";
import { useWorkspace } from './WorkspaceContext';
import { sanitizeText } from './utils/sanitize';
import CommentBox from './CommentBox';
import {
  Search, Filter, Plus, X, AlertTriangle,
  CheckCircle2, FileText, Mail,
  Phone, Linkedin, Star, ArrowUpRight,
  PlusCircle, Loader2, Trash2
} from "lucide-react";

const SECTORS = ["SaaS", "Fintech", "Healthtech", "Edtech", "CleanTech", "E-commerce", "AI/ML", "Biotech", "Consumer", "Other"];
const STAGES = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C+"];
const HEALTH = ["Healthy", "Watch", "At Risk"];

function fmt(n) {
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K`;
  return `₹${n}`;
}

function healthConfig(health) {
  if (health === "Healthy") return { bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" };
  if (health === "Watch") return { bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-500" };
  return { bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" };
}

export default function Portfolio() {
  const { logActivity } = useWorkspace() || {};
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [search, setSearch] = useState("");
  const [filterSector, setFilterSector] = useState("All");
  const [filterStage, setFilterStage] = useState("All");
  const [filterHealth, setFilterHealth] = useState("All");
  const [sortBy, setSortBy] = useState("name");
  const [showAddModal, setShowAddModal] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [newTask, setNewTask] = useState("");
  const [newTaskDue, setNewTaskDue] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const docInputRef = useRef(null);

  // Load companies from Supabase on mount
  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) { setLoading(false); return; }

    const { data, error } = await supabase
      .from("companies")
      .select("*")
      .eq("user_id", user.id)
      .order("created_at", { ascending: false });

    if (error) { console.error("Error fetching companies:", error); setLoading(false); return; }

    // Parse JSON fields
    const parsed = (data || []).map(c => ({
      ...c,
      founders: c.founders || [],
      coInvestors: c.co_investors || [],
      fundingHistory: c.funding_history || [],
      metrics: c.metrics || [],
      notes: c.notes || [],
      nextSteps: c.next_steps || [],
      documents: c.documents || [],
      starred: c.starred || false,
      color: c.color || "bg-blue-600",
      logo: c.name?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "CO",
    }));

    setCompanies(parsed);
    setLoading(false);
  };

  const saveCompanyToDB = async (company) => {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return null;

    const { data, error } = await supabase.from("companies").insert({
      user_id: user.id,
      name: sanitizeText(company.name),
      sector: sanitizeText(company.sector),
      stage: sanitizeText(company.stage),
      health: company.health,
      runway: company.runway,
      mrr: company.revenue / 12,
      burn: company.burn,
      arr: company.revenue,
      location: sanitizeText(company.location),
      website: sanitizeText(company.website),
      description: sanitizeText(company.description),
      founded_year: company.founded,
      color: company.color,
      starred: company.starred,
      founders: company.founders,
      co_investors: company.coInvestors,
      funding_history: company.fundingHistory,
      metrics: company.metrics,
      notes: company.notes,
      next_steps: company.nextSteps,
      documents: company.documents,
      invested: company.invested,
      valuation: company.valuation,
      growth: company.growth,
    }).select().single();

    if (error) { console.error("Error saving company:", error); return null; }

    // fire-and-forget embed
    const embedContent = [
      `Company: ${company.name}`,
      `Sector: ${company.sector}`,
      `Stage: ${company.stage}`,
      `Health: ${company.health}`,
      `ARR: ${company.arr || company.revenue}`,
      `Runway: ${company.runway} months`,
      `Burn: ${company.burn}`,
      `Description: ${company.description}`,
      `Notes: ${(company.notes || []).map(n => n.text).join('. ')}`,
    ].join('\n');
    fetch('/api/rag', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token}`
      },
      body: JSON.stringify({
        operation: 'embed',
        content: embedContent,
        contentType: 'portfolio_company',
        companyName: company.name,
        sector: company.sector,
        stage: company.stage,
      })
    }).catch(() => {});

    return data;
  };

  const updateCompanyInDB = async (company) => {
    const { error } = await supabase.from("companies").update({
      health: company.health,
      runway: company.runway,
      starred: company.starred,
      notes: company.notes,
      next_steps: company.nextSteps,
      metrics: company.metrics,
    }).eq("id", company.id);
    if (error) console.error("Error updating company:", error);

    // fire-and-forget embed
    supabase.auth.getSession().then(({ data: { session } }) => {
      const embedContent = [
        `Company: ${company.name}`,
        `Sector: ${company.sector}`,
        `Stage: ${company.stage}`,
        `Health: ${company.health}`,
        `ARR: ${company.arr}`,
        `Runway: ${company.runway} months`,
        `Burn: ${company.burn}`,
        `Description: ${company.description}`,
        `Notes: ${(company.notes || []).map(n => n.text).join('. ')}`,
      ].join('\n');
      fetch('/api/rag', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          operation: 'embed',
          content: embedContent,
          contentType: 'portfolio_company',
          companyName: company.name,
          sector: company.sector,
          stage: company.stage,
        })
      }).catch(() => {});
    });
  };

 

  // Filter & sort
  const filtered = companies
    .filter(c => {
      const matchSearch = !search || c.name.toLowerCase().includes(search.toLowerCase());
      const matchSector = filterSector === "All" || c.sector === filterSector;
      const matchStage = filterStage === "All" || c.stage === filterStage;
      const matchHealth = filterHealth === "All" || c.health === filterHealth;
      return matchSearch && matchSector && matchStage && matchHealth;
    })
    .sort((a, b) => {
      if (sortBy === "name") return a.name.localeCompare(b.name);
      if (sortBy === "runway") return (a.runway || 0) - (b.runway || 0);
      if (sortBy === "multiple") return ((b.valuation / b.invested) || 0) - ((a.valuation / a.invested) || 0);
      return 0;
    });

  const totalInvested = companies.reduce((s, c) => s + (c.invested || 0), 0);
  const totalValuation = companies.reduce((s, c) => s + (c.valuation || 0), 0);
  const atRisk = companies.filter(c => c.health === "At Risk").length;

  const toggleStar = async (id) => {
    const updated = companies.map(c => c.id === id ? { ...c, starred: !c.starred } : c);
    setCompanies(updated);
    if (selectedCompany?.id === id) setSelectedCompany(prev => ({ ...prev, starred: !prev.starred }));
    const company = updated.find(c => c.id === id);
    await updateCompanyInDB(company);
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    const note = { date: new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }), text: sanitizeText(newNote), author: "You" };
    const updatedNotes = [note, ...(selectedCompany.notes || [])];
    const updatedCompany = { ...selectedCompany, notes: updatedNotes };
    setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? updatedCompany : c));
    setSelectedCompany(updatedCompany);
    setNewNote("");
    await updateCompanyInDB(updatedCompany);
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    const task = { task: newTask, due: newTaskDue || "No due date", done: false };
    const updatedSteps = [...(selectedCompany.nextSteps || []), task];
    const updatedCompany = { ...selectedCompany, nextSteps: updatedSteps };
    setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? updatedCompany : c));
    setSelectedCompany(updatedCompany);
    setNewTask(""); setNewTaskDue("");
    await updateCompanyInDB(updatedCompany);
  };

  const toggleTask = async (idx) => {
    const updated = (selectedCompany.nextSteps || []).map((t, i) => i === idx ? { ...t, done: !t.done } : t);
    const updatedCompany = { ...selectedCompany, nextSteps: updated };
    setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? updatedCompany : c));
    setSelectedCompany(updatedCompany);
    await updateCompanyInDB(updatedCompany);
  };

  const handleDeleteCompany = async (companyId) => {
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', companyId);

    if (!error) {
      setCompanies(prev => prev.filter(c => c.id !== companyId));
      if (selectedCompany?.id === companyId) {
        setSelectedCompany(null);
      }
    } else {
      alert('Failed to delete. Please try again.');
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedCompany) return;
    setDocUploading(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setDocUploading(false); return; }
    const ext = file.name.split('.').pop();
    const path = `${session.user.id}/${selectedCompany.id}/${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from('company-documents').upload(path, file);
    if (uploadError) { console.error('Upload error:', uploadError); setDocUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from('company-documents').getPublicUrl(path);
    const doc = {
      name: file.name,
      type: ext.toUpperCase(),
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      url: publicUrl,
    };
    const updatedDocs = [...(selectedCompany.documents || []), doc];
    const updatedCompany = { ...selectedCompany, documents: updatedDocs };
    setSelectedCompany(updatedCompany);
    setCompanies(prev => prev.map(c => c.id === selectedCompany.id ? updatedCompany : c));
    await supabase.from('companies').update({ documents: updatedDocs }).eq('id', selectedCompany.id);
    e.target.value = '';
    setDocUploading(false);
  };

  const TABS = ["overview", "metrics", "founders", "funding", "notes", "tasks", "documents"];

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "50vh", flexDirection: "column", gap: 16 }}>
        <Loader2 style={{ width: 32, height: 32, color: "#dc2626" }} className="animate-spin" />
        <p style={{ fontSize: 14, fontWeight: 600, color: "rgba(255,255,255,0.4)" }}>Loading portfolio...</p>
      </div>
    );
  }

  return (
    <div className="flex gap-6 animate-in fade-in duration-500" style={{ minHeight: "80vh" }}>

      {/* Left Panel */}
      <div className={`flex flex-col gap-4 transition-all duration-300 ${selectedCompany ? "w-96 flex-shrink-0" : "flex-1"}`}>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: "Portfolio Value", value: fmt(totalValuation), color: "text-white" },
            { label: "Total Invested", value: fmt(totalInvested), color: "text-red-400" },
            { label: "At Risk", value: atRisk, color: atRisk > 0 ? "text-red-400" : "text-white" },
          ].map((s, i) => (
            <div key={i} style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "16px", textAlign: "center" }}>
              <p style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 6px" }}>{s.label}</p>
              <p className={`text-xl font-black mt-1 ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Search + Filter + Add */}
        <div className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "rgba(255,255,255,0.25)" }} />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search companies..."
              style={{ width: "100%", paddingLeft: 44, paddingRight: 16, paddingTop: 12, paddingBottom: 12, background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, outline: "none", fontSize: 13, fontWeight: 500, color: "#fff", fontFamily: "inherit", boxSizing: "border-box" }}
              onFocus={e => e.target.style.borderColor = "#dc2626"}
              onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.06)"} />
          </div>
          <button onClick={() => setShowFilters(!showFilters)}
            style={{ padding: "12px 14px", background: showFilters ? "#dc2626" : "#1a1a1a", border: showFilters ? "1px solid #dc2626" : "1px solid rgba(255,255,255,0.06)", borderRadius: 12, cursor: "pointer", color: showFilters ? "#fff" : "rgba(255,255,255,0.4)" }}>
            <Filter style={{ width: 16, height: 16 }} />
          </button>
          <button onClick={() => setShowAddModal(true)}
            style={{ padding: "12px 16px", background: "#dc2626", border: "none", borderRadius: 12, cursor: "pointer", color: "#fff", display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, fontFamily: "inherit" }}>
            <Plus style={{ width: 16, height: 16 }} />
            {!selectedCompany && <span>Add</span>}
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: 16, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {[
              { label: "Sector", value: filterSector, set: setFilterSector, options: ["All", ...SECTORS] },
              { label: "Stage", value: filterStage, set: setFilterStage, options: ["All", ...STAGES] },
              { label: "Health", value: filterHealth, set: setFilterHealth, options: ["All", ...HEALTH] },
              { label: "Sort By", value: sortBy, set: setSortBy, options: [{ v: "name", l: "Name" }, { v: "runway", l: "Runway" }, { v: "multiple", l: "Multiple" }] },
            ].map((f, i) => (
              <div key={i}>
                <label style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 }}>{f.label}</label>
                <select value={f.value} onChange={e => f.set(e.target.value)}
                  style={{ width: "100%", padding: "8px 10px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "#fff", outline: "none", fontFamily: "inherit" }}>
                  {f.options.map(o => typeof o === "string" ? <option key={o} value={o}>{o}</option> : <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}

        {/* Company Cards */}
        <div className={`grid gap-4 overflow-y-auto ${selectedCompany ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"}`}>
          {filtered.length === 0 && !loading && (
            <div style={{ background: "#1a1a1a", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, padding: "48px 24px", textAlign: "center", gridColumn: "1 / -1" }}>
              <p style={{ fontSize: 14, fontWeight: 700, color: "rgba(255,255,255,0.2)", margin: "0 0 6px" }}>No companies yet</p>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,0.15)", fontWeight: 500, margin: 0 }}>Click Add to add your first portfolio company</p>
            </div>
          )}
          {filtered.map(company => {
            const h = healthConfig(company.health);
            const multiple = company.invested > 0 ? (company.valuation / company.invested).toFixed(2) : "N/A";
            return (
              <div key={company.id} onClick={() => { setSelectedCompany(company); setActiveTab("overview"); }}
                style={{ background: selectedCompany?.id === company.id ? "rgba(220,38,38,0.08)" : "#1a1a1a", border: selectedCompany?.id === company.id ? "1px solid rgba(220,38,38,0.4)" : company.runway < 6 ? "1px solid rgba(220,38,38,0.2)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 16, cursor: "pointer", transition: "all 0.15s", padding: 20 }}
                onMouseEnter={e => { if (selectedCompany?.id !== company.id) e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; }}
                onMouseLeave={e => { if (selectedCompany?.id !== company.id) e.currentTarget.style.borderColor = company.runway < 6 ? "rgba(220,38,38,0.2)" : "rgba(255,255,255,0.06)"; }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div className={`w-9 h-9 ${company.color} rounded-xl flex items-center justify-center text-white font-black text-xs flex-shrink-0`}>
                      {company.logo}
                    </div>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 800, color: "#fff", margin: 0, letterSpacing: "-0.2px" }}>{company.name}</p>
                      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", margin: 0, fontWeight: 500 }}>{company.stage} · {company.sector}</p>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete ${company.name}? This cannot be undone.`)) {
                          handleDeleteCompany(company.id);
                        }
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'rgba(255,255,255,0.5)',
                        padding: '2px',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'color 0.15s'
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = '#dc2626'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                    >
                      <Trash2 style={{ width: 15, height: 15 }} />
                    </button>
                    <button onClick={e => { e.stopPropagation(); toggleStar(company.id); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: company.starred ? "#eab308" : "rgba(255,255,255,0.2)" }}>
                      <Star style={{ width: 14, height: 14 }} fill={company.starred ? "currentColor" : "none"} />
                    </button>
                    <span className={`text-[9px] font-black uppercase px-2 py-1 rounded-lg ${h.bg} ${h.text}`}>{company.health}</span>
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: company.runway < 6 ? 10 : 0 }}>
                  {[
                    { label: "Invested", value: fmt(company.invested || 0) },
                    { label: "Multiple", value: `${multiple}x` },
                    { label: "Runway", value: `${company.runway || 0}mo` },
                  ].map((m, i) => (
                    <div key={i} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 9, padding: "8px", textAlign: "center" }}>
                      <p style={{ fontSize: 8, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", margin: "0 0 3px" }}>{m.label}</p>
                      <p style={{ fontSize: 12, fontWeight: 900, color: m.label === "Runway" && company.runway < 6 ? "#dc2626" : "#fff", margin: 0 }}>{m.value}</p>
                    </div>
                  ))}
                </div>

                {company.runway < 6 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(220,38,38,0.08)", borderRadius: 8, padding: "7px 10px" }}>
                    <AlertTriangle style={{ width: 11, height: 11, color: "#dc2626", flexShrink: 0 }} />
                    <p style={{ fontSize: 9, fontWeight: 700, color: "#dc2626", margin: 0 }}>Critical runway — action needed</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Right Detail Panel */}
      {selectedCompany && (
        <div style={{ flex: 1, background: "#161616", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 16, overflow: "hidden", display: "flex", flexDirection: "column" }} className="animate-in slide-in-from-right-4 duration-300">

          {/* Header */}
          <div style={{ padding: "24px 28px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div className={`w-12 h-12 ${selectedCompany.color} rounded-2xl flex items-center justify-center text-white font-black text-sm`}>
                  {selectedCompany.logo}
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <h2 style={{ fontSize: 20, fontWeight: 900, color: "#fff", margin: 0, letterSpacing: "-0.5px" }}>{selectedCompany.name}</h2>
                    {selectedCompany.website && (
                      <a href={`https://${selectedCompany.website}`} target="_blank" rel="noreferrer" style={{ color: "rgba(255,255,255,0.3)", transition: "color 0.2s" }}
                        onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
                        onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.3)"}>
                        <ArrowUpRight style={{ width: 16, height: 16 }} />
                      </a>
                    )}
                  </div>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", margin: 0, fontWeight: 500 }}>{selectedCompany.location} · Founded {selectedCompany.founded_year || selectedCompany.founded}</p>
                </div>
              </div>
              <button onClick={() => setSelectedCompany(null)} style={{ padding: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
                <X style={{ width: 15, height: 15 }} />
              </button>
            </div>
            {selectedCompany.description && <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", marginTop: 12, lineHeight: 1.6, fontWeight: 400 }}>{selectedCompany.description}</p>}
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,0.06)", overflowX: "auto" }}>
            {TABS.map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                style={{ padding: "14px 18px", fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", whiteSpace: "nowrap", background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", borderBottom: activeTab === tab ? "2px solid #dc2626" : "2px solid transparent", color: activeTab === tab ? "#dc2626" : "rgba(255,255,255,0.3)", transition: "color 0.15s" }}>
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px" }}>

            {activeTab === "overview" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
                  {[
                    { label: "Invested", value: fmt(selectedCompany.invested || 0) },
                    { label: "Valuation", value: fmt(selectedCompany.valuation || 0) },
                    { label: "Multiple", value: `${selectedCompany.invested > 0 ? (selectedCompany.valuation / selectedCompany.invested).toFixed(2) : "N/A"}x` },
                    { label: "Runway", value: `${selectedCompany.runway || 0}mo`, alert: selectedCompany.runway < 6 },
                    { label: "ARR", value: fmt(selectedCompany.arr || selectedCompany.revenue || 0) },
                    { label: "Monthly Burn", value: fmt(selectedCompany.burn || 0) },
                    { label: "MoM Growth", value: `${selectedCompany.growth || 0}%` },
                    { label: "Stage", value: selectedCompany.stage },
                  ].map((m, i) => (
                    <div key={i} style={{ padding: "14px 16px", background: m.alert ? "rgba(220,38,38,0.08)" : "rgba(255,255,255,0.03)", border: m.alert ? "1px solid rgba(220,38,38,0.2)" : "1px solid rgba(255,255,255,0.06)", borderRadius: 12 }}>
                      <p style={{ fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 6px" }}>{m.label}</p>
                      <p style={{ fontSize: 16, fontWeight: 900, color: m.alert ? "#dc2626" : "#fff", margin: 0, letterSpacing: "-0.3px" }}>{m.value}</p>
                    </div>
                  ))}
                </div>
                {(selectedCompany.coInvestors?.length > 0) && (
                  <div>
                    <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 10px" }}>Co-Investors</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {selectedCompany.coInvestors.map((inv, i) => (
                        <span key={i} style={{ padding: "6px 12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,0.6)" }}>{inv}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === "metrics" && (
              <div>
                <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 16px" }}>Monthly Performance</p>
                {selectedCompany.metrics?.length > 0 ? (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                        {["Month", "ARR", "Monthly Burn"].map((h, i) => (
                          <th key={i} style={{ padding: "10px 16px", textAlign: "left", fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {selectedCompany.metrics.map((m, i) => (
                        <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.04)" }}>
                          <td style={{ padding: "12px 16px", fontSize: 13, fontWeight: 700, color: "#fff" }}>{m.month}</td>
                          <td style={{ padding: "12px 16px", fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{fmt(m.arr)}</td>
                          <td style={{ padding: "12px 16px", fontSize: 13, color: "rgba(255,255,255,0.5)" }}>{fmt(m.burn)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : <p style={{ fontSize: 13, color: "rgba(255,255,255,0.2)", fontWeight: 500 }}>No metrics added yet.</p>}
              </div>
            )}

            {activeTab === "founders" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {selectedCompany.founders?.length > 0 ? selectedCompany.founders.map((f, i) => (
                  <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 14, padding: "20px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                      <div className={`w-10 h-10 ${selectedCompany.color} rounded-xl flex items-center justify-center text-white font-black text-xs`}>
                        {f.name?.split(" ").map(n => n[0]).join("")}
                      </div>
                      <div>
                        <p style={{ fontSize: 14, fontWeight: 800, color: "#fff", margin: 0 }}>{f.name}</p>
                        <p style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>{f.role}</p>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      {f.email && <a href={`mailto:${f.email}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "rgba(255,255,255,0.5)", textDecoration: "none", fontWeight: 500 }}><Mail style={{ width: 13, height: 13 }} />{f.email}</a>}
                      {f.phone && <a href={`tel:${f.phone}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "rgba(255,255,255,0.5)", textDecoration: "none", fontWeight: 500 }}><Phone style={{ width: 13, height: 13 }} />{f.phone}</a>}
                      {f.linkedin && <a href={`https://${f.linkedin}`} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "rgba(255,255,255,0.5)", textDecoration: "none", fontWeight: 500 }}><Linkedin style={{ width: 13, height: 13 }} />{f.linkedin}</a>}
                    </div>
                  </div>
                )) : <p style={{ fontSize: 13, color: "rgba(255,255,255,0.2)", fontWeight: 500 }}>No founder details added.</p>}
              </div>
            )}

            {activeTab === "funding" && (
              <div>
                <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 20px" }}>Funding Timeline</p>
                {selectedCompany.fundingHistory?.length > 0 ? (
                  <div style={{ position: "relative" }}>
                    <div style={{ position: "absolute", left: 16, top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.06)" }} />
                    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                      {selectedCompany.fundingHistory.map((f, i) => (
                        <div key={i} style={{ paddingLeft: 48, position: "relative" }}>
                          <div style={{ position: "absolute", left: 8, top: 4, width: 18, height: 18, background: "#dc2626", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <div style={{ width: 6, height: 6, background: "#fff", borderRadius: "50%" }} />
                          </div>
                          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "16px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <p style={{ fontSize: 14, fontWeight: 800, color: "#fff", margin: "0 0 4px" }}>{f.round}</p>
                              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", margin: 0, fontWeight: 500 }}>{f.date}</p>
                            </div>
                            <div style={{ textAlign: "right" }}>
                              <p style={{ fontSize: 18, fontWeight: 900, color: "#dc2626", margin: "0 0 2px", letterSpacing: "-0.5px" }}>{fmt(f.amount)}</p>
                              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0, fontWeight: 500 }}>Val: {fmt(f.valuation)}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : <p style={{ fontSize: 13, color: "rgba(255,255,255,0.2)", fontWeight: 500 }}>No funding history added.</p>}
              </div>
            )}

            {activeTab === "notes" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <div style={{ display: "flex", gap: 10 }}>
                  <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a meeting note..." rows={2}
                    style={{ flex: 1, padding: "12px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, outline: "none", fontSize: 13, fontWeight: 500, color: "#fff", resize: "none", fontFamily: "inherit", lineHeight: 1.5 }}
                    onFocus={e => e.target.style.borderColor = "#dc2626"}
                    onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
                  <button onClick={addNote} style={{ padding: "0 16px", background: "#dc2626", border: "none", borderRadius: 12, fontSize: 12, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit", alignSelf: "flex-end", height: 40 }}>Add</button>
                </div>
                {(selectedCompany.notes || []).map((n, i) => (
                  <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: "14px 16px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                      <p style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", textTransform: "uppercase", letterSpacing: "0.1em", margin: 0 }}>{n.author}</p>
                      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0, fontWeight: 500 }}>{n.date}</p>
                    </div>
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", margin: 0, lineHeight: 1.6, fontWeight: 400 }}>{n.text}</p>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "tasks" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={{ display: "flex", gap: 10 }}>
                  <input type="text" value={newTask} onChange={e => setNewTask(e.target.value)} placeholder="Add next step..."
                    style={{ flex: 1, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, outline: "none", fontSize: 13, fontWeight: 500, color: "#fff", fontFamily: "inherit" }}
                    onFocus={e => e.target.style.borderColor = "#dc2626"}
                    onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
                  <input type="date" value={newTaskDue} onChange={e => setNewTaskDue(e.target.value)}
                    style={{ padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, outline: "none", fontSize: 12, fontWeight: 500, color: "#fff", fontFamily: "inherit" }}
                    onFocus={e => e.target.style.borderColor = "#dc2626"}
                    onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
                  <button onClick={addTask} style={{ padding: "10px 16px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 12, fontWeight: 800, color: "#fff", cursor: "pointer", fontFamily: "inherit" }}>Add</button>
                </div>
                {(selectedCompany.nextSteps || []).map((t, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 16px", background: t.done ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, opacity: t.done ? 0.6 : 1 }}>
                    <button onClick={() => toggleTask(i)} style={{ width: 20, height: 20, borderRadius: "50%", border: t.done ? "none" : "2px solid rgba(255,255,255,0.2)", background: t.done ? "#16a34a" : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                      {t.done && <CheckCircle2 style={{ width: 12, height: 12, color: "#fff" }} />}
                    </button>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 600, color: t.done ? "rgba(255,255,255,0.3)" : "#fff", margin: "0 0 2px", textDecoration: t.done ? "line-through" : "none" }}>{t.task}</p>
                      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.25)", margin: 0, fontWeight: 500 }}>Due: {t.due}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === "documents" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input
                  type="file"
                  ref={docInputRef}
                  style={{ display: 'none' }}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={handleDocumentUpload}
                />
                {(selectedCompany.documents || []).map((d, i) => (
                  <a key={i} href={d.url} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 16px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, cursor: "pointer", textDecoration: "none" }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(220,38,38,0.3)"}
                    onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"}>
                    <div style={{ width: 36, height: 36, background: "rgba(220,38,38,0.1)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <FileText style={{ width: 16, height: 16, color: "#dc2626" }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: 13, fontWeight: 700, color: "#fff", margin: "0 0 2px" }}>{d.name}</p>
                      <p style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", margin: 0, fontWeight: 500 }}>{d.type} · {d.date}</p>
                    </div>
                    <ArrowUpRight style={{ width: 14, height: 14, color: "rgba(255,255,255,0.2)" }} />
                  </a>
                ))}
                <button
                  onClick={() => docInputRef.current?.click()}
                  disabled={docUploading}
                  style={{ width: "100%", padding: "14px", border: "1px dashed rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12, fontWeight: 700, color: docUploading ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.3)", background: "none", cursor: docUploading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontFamily: "inherit", transition: "all 0.2s" }}
                  onMouseEnter={e => { if (!docUploading) { e.currentTarget.style.borderColor = "rgba(220,38,38,0.3)"; e.currentTarget.style.color = "#dc2626"; } }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)"; e.currentTarget.style.color = "rgba(255,255,255,0.3)"; }}>
                  {docUploading ? <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" /> : <PlusCircle style={{ width: 14, height: 14 }} />}
                  {docUploading ? "Uploading..." : "Upload Document"}
                </button>
              </div>
            )}
          </div>
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '16px 28px 20px', flexShrink: 0, background: 'rgba(0,0,0,0.15)' }}>
            <CommentBox
              entityType="company"
              entityId={selectedCompany.id}
              entityName={selectedCompany.name}
            />
          </div>
        </div>
      )}

      {/* Add Company Modal */}
      {showAddModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 24 }}>
          <div style={{ background: "#161616", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto" }}>
            <div style={{ padding: "28px 32px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
                <div>
                  <h3 style={{ fontSize: 20, fontWeight: 900, color: "#fff", margin: "0 0 4px", letterSpacing: "-0.5px" }}>Add Company</h3>
                  <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", margin: 0 }}>Add a new portfolio company</p>
                </div>
                <button onClick={() => setShowAddModal(false)} style={{ padding: 8, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 9, cursor: "pointer", color: "rgba(255,255,255,0.4)" }}>
                  <X style={{ width: 15, height: 15 }} />
                </button>
              </div>
              <AddCompanyForm
                onSave={async (company) => {
                  const saved = await saveCompanyToDB(company);
                  if (saved) {
                    const newCompany = {
                      ...company,
                      id: saved.id,
                      logo: company.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2),
                    };
                    setCompanies(prev => [newCompany, ...prev]);
                    logActivity?.('added_company', 'company', saved.id, company.name, { sector: company.sector, stage: company.stage });
                  }
                  setShowAddModal(false);
                }}
                onCancel={() => setShowAddModal(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AddCompanyForm({ onSave, onCancel }) {
  const COLORS = ["bg-blue-600", "bg-red-600", "bg-green-600", "bg-purple-600", "bg-orange-600", "bg-pink-600", "bg-indigo-600", "bg-teal-600"];
  const [form, setForm] = useState({
    name: "", sector: "SaaS", stage: "Seed", health: "Healthy",
    invested: "", valuation: "", runway: "", burn: "", revenue: "", growth: "",
    founded: "", website: "", location: "", description: "", color: "bg-blue-600",
    founderName: "", founderRole: "CEO", founderEmail: "", founderPhone: "", founderLinkedin: "",
    coInvestors: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const inputStyle = {
    width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, outline: "none",
    fontSize: 13, fontWeight: 500, color: "#fff", fontFamily: "inherit", boxSizing: "border-box"
  };
  const labelStyle = { fontSize: 9, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: 6 };

  const handleSave = async () => {
    if (!form.name.trim()) { alert("Company name is required"); return; }
    setSaving(true);
    const logo = form.name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
    const newCompany = {
      name: form.name, logo, color: form.color, sector: form.sector, stage: form.stage,
      health: form.health, invested: parseFloat(form.invested) || 0, valuation: parseFloat(form.valuation) || 0,
      runway: parseInt(form.runway) || 0, burn: parseFloat(form.burn) || 0, revenue: parseFloat(form.revenue) || 0,
      growth: parseFloat(form.growth) || 0, founded: form.founded, website: form.website, location: form.location,
      description: form.description,
      founders: form.founderName ? [{ name: form.founderName, role: form.founderRole, email: form.founderEmail, phone: form.founderPhone, linkedin: form.founderLinkedin }] : [],
      coInvestors: form.coInvestors ? form.coInvestors.split(",").map(s => s.trim()).filter(Boolean) : [],
      fundingHistory: form.invested ? [{ round: form.stage, amount: parseFloat(form.invested) || 0, date: new Date().toLocaleDateString("en-US", { month: "short", year: "numeric" }), valuation: parseFloat(form.valuation) || 0 }] : [],
      metrics: [], notes: [], nextSteps: [], documents: [], starred: false,
    };
    await onSave(newCompany);
    setSaving(false);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Color */}
      <div>
        <label style={labelStyle}>Company Color</label>
        <div style={{ display: "flex", gap: 8 }}>
          {COLORS.map(c => (
            <button key={c} onClick={() => set("color", c)} className={`w-7 h-7 rounded-lg ${c} transition`}
              style={{ border: form.color === c ? "2px solid #fff" : "2px solid transparent", transform: form.color === c ? "scale(1.15)" : "scale(1)" }} />
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Company Name *</label>
          <input value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. NexaFlow" style={inputStyle} onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
        </div>
        {[
          { label: "Sector", key: "sector", type: "select", options: SECTORS },
          { label: "Stage", key: "stage", type: "select", options: STAGES },
          { label: "Health Status", key: "health", type: "select", options: HEALTH },
          { label: "Founded Year", key: "founded", placeholder: "2022" },
          { label: "Location", key: "location", placeholder: "Mumbai, India" },
          { label: "Website", key: "website", placeholder: "company.com" },
        ].map(f => (
          <div key={f.key}>
            <label style={labelStyle}>{f.label}</label>
            {f.type === "select" ? (
              <select value={form[f.key]} onChange={e => set(f.key, e.target.value)} style={{ ...inputStyle, cursor: "pointer" }}>
                {f.options.map(o => <option key={o}>{o}</option>)}
              </select>
            ) : (
              <input value={form[f.key]} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} style={inputStyle} onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            )}
          </div>
        ))}
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>Description</label>
          <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={2} placeholder="What does this company do?" style={{ ...inputStyle, resize: "none" }} onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
        </div>
      </div>

      <div>
        <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 12px" }}>Financials</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            { label: "Amount Invested (₹)", key: "invested", placeholder: "2500000" },
            { label: "Current Valuation (₹)", key: "valuation", placeholder: "12500000" },
            { label: "Runway (months)", key: "runway", placeholder: "18" },
            { label: "Monthly Burn (₹)", key: "burn", placeholder: "120000" },
            { label: "ARR (₹)", key: "revenue", placeholder: "1000000" },
            { label: "MoM Growth (%)", key: "growth", placeholder: "15" },
          ].map(f => (
            <div key={f.key}>
              <label style={labelStyle}>{f.label}</label>
              <input type="number" value={form[f.key]} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} style={inputStyle} onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <p style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.1em", margin: "0 0 12px" }}>Lead Founder</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {[
            { label: "Full Name", key: "founderName", placeholder: "Rahul Mehta" },
            { label: "Role", key: "founderRole", placeholder: "CEO" },
            { label: "Email", key: "founderEmail", placeholder: "rahul@company.com" },
            { label: "Phone", key: "founderPhone", placeholder: "+91 98765 43210" },
            { label: "LinkedIn", key: "founderLinkedin", placeholder: "linkedin.com/in/rahul" },
          ].map(f => (
            <div key={f.key}>
              <label style={labelStyle}>{f.label}</label>
              <input value={form[f.key]} onChange={e => set(f.key, e.target.value)} placeholder={f.placeholder} style={inputStyle} onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>Co-Investors (comma separated)</label>
        <input value={form.coInvestors} onChange={e => set("coInvestors", e.target.value)} placeholder="Sequoia, Blume Ventures, Accel" style={inputStyle} onFocus={e => e.target.style.borderColor = "#dc2626"} onBlur={e => e.target.style.borderColor = "rgba(255,255,255,0.08)"} />
      </div>

      <div style={{ display: "flex", gap: 10, paddingTop: 8 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: "12px", background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "rgba(255,255,255,0.5)", cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
        <button onClick={handleSave} disabled={saving} style={{ flex: 1, padding: "12px", background: "#dc2626", border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700, color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: saving ? 0.7 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {saving && <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />}
          {saving ? "Saving..." : "Add Company"}
        </button>
      </div>
    </div>
  );
}
